import { DIRS8, ORTHO, chebyshev, offset, parseSq, sqName } from './geometry';
import { ABILITY_INFO } from './presets';
import type {
  AbilityDef, AbilityKind, Action, Color, GameEvent, GameResult, GameState, Mine, Piece, PieceTypeDef, Ruleset,
} from './types';

export const opp = (c: Color): Color => (c === 'w' ? 'b' : 'w');

// ---------------------------------------------------------------- setup

export function createGame(input: Ruleset): GameState {
  const rs: Ruleset = JSON.parse(JSON.stringify(input));
  const w = rs.board.width;
  const h = rs.board.height;
  const cells = new Array<number>(w * h).fill(0);
  for (const v of rs.board.voids) { const sq = parseSq(v, w, h); if (sq >= 0) cells[sq] = 1; }
  for (const v of rs.board.walls) { const sq = parseSq(v, w, h); if (sq >= 0) cells[sq] = 2; }
  const board: (Piece | null)[] = new Array(w * h).fill(null);
  let nextId = 1;
  for (const p of rs.setup) {
    const sq = parseSq(p.sq, w, h);
    if (sq < 0 || cells[sq] !== 0 || board[sq] || !rs.pieces[p.type]) continue;
    board[sq] = { id: nextId++, type: p.type, color: p.color, moved: false, cd: {}, exhausted: 0, fuse: 0 };
  }
  const s: GameState = {
    rs, w, h, cells, board,
    mines: [], burning: [], holes: [], hedges: [],
    turn: 'w',
    actionsLeft: Math.max(1, rs.rules.movesPerTurn),
    turnNumber: 1, ply: 0, ep: null, halfmove: 0, history: [], result: null, nextId,
    royalsStart: { w: 0, b: 0 },
  };
  s.royalsStart = { w: royalCount(s, 'w'), b: royalCount(s, 'b') };
  s.history = [positionHash(s)];
  return s;
}

export function cloneState(s: GameState): GameState {
  return {
    ...s,
    board: s.board.slice(),
    mines: s.mines.slice(),
    burning: s.burning.slice(),
    holes: s.holes.slice(),
    hedges: (s.hedges ?? []).slice(),
    history: s.history.slice(),
    royalsStart: { ...s.royalsStart },
  };
}

// ---------------------------------------------------------------- helpers

export const defOf = (s: GameState, p: Piece): PieceTypeDef => s.rs.pieces[p.type];

export function ability(def: PieceTypeDef | undefined, kind: AbilityKind): AbilityDef | undefined {
  return def?.abilities.find((a) => a.kind === kind && a.enabled);
}

export const isHole = (s: GameState, sq: number) => s.cells[sq] === 1 || s.holes.some((h) => h.sq === sq);
const isHedge = (s: GameState, sq: number) => !!s.hedges?.some((h) => h.sq === sq);
export const isWall = (s: GameState, sq: number) => s.cells[sq] === 2 || isHedge(s, sq);
export const isStandable = (s: GameState, sq: number) => s.cells[sq] === 0 && !s.holes.some((h) => h.sq === sq) && !isHedge(s, sq);

export function royalCount(s: GameState, color: Color): number {
  let n = 0;
  for (const p of s.board) if (p && p.color === color && s.rs.pieces[p.type]?.royal) n++;
  return n;
}

function pieceCount(s: GameState, color: Color): number {
  let n = 0;
  for (const p of s.board) if (p && p.color === color) n++;
  return n;
}

export function inZone(s: GameState, sq: number, color: Color): boolean {
  const y = Math.floor(sq / s.w);
  const z = Math.max(1, s.rs.rules.promotion.zone);
  return color === 'w' ? y >= s.h - z : y < z;
}

function neighbors(s: GameState, from: number, range: number): number[] {
  const out: number[] = [];
  for (let dy = -range; dy <= range; dy++)
    for (let dx = -range; dx <= range; dx++) {
      if (!dx && !dy) continue;
      const sq = offset(from, dx, dy, s.w, s.h);
      if (sq >= 0) out.push(sq);
    }
  return out;
}

export function mineVisibleTo(s: GameState, m: Mine, color: Color | null): boolean {
  if (m.revealed) return true;
  if (!color) return false;
  if (m.owner === color) return true;
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (!p || p.color !== color) continue;
    const r = s.rs.pieces[p.type]?.detectRadius ?? 0;
    if (r > 0 && chebyshev(sq, m.sq, s.w) <= r) return true;
  }
  return false;
}

/** The state as seen by one player (or a spectator): hides unseen enemy mines. */
export function viewFor(s: GameState, color: Color | null): GameState {
  return { ...s, mines: s.mines.filter((m) => mineVisibleTo(s, m, color)) };
}

export function positionHash(s: GameState): string {
  let out = s.turn + s.actionsLeft + (s.ep ? s.ep.target : '-') + '|';
  for (const p of s.board) {
    if (!p) { out += '.'; continue; }
    const d = s.rs.pieces[p.type];
    const movedMatters = d && (d.castles || d.castlePartner || d.moves.some((m) => m.firstMoveOnly));
    out += p.color + p.type + (movedMatters && p.moved ? 1 : 0) + (p.fuse || '') + (p.exhausted || '');
    for (const k in p.cd) if (p.cd[k as AbilityKind]) out += k[0] + p.cd[k as AbilityKind];
    out += ',';
  }
  out += '|' + s.mines.map((m) => m.sq).join(',') + '|' + s.holes.map((h) => h.sq + ':' + h.left).join(',') + '|' + (s.hedges ?? []).map((h) => h.sq + ':' + h.left).join(',');
  return out;
}

// ---------------------------------------------------------------- move generation

/**
 * Squares an elephant charge passes through in one direction: it runs over
 * enemies and stops before its own pieces, walls, hedges and holes.
 */
function stampedeLine(s: GameState, from: number, color: Color, dx: number, dy: number, range: number): number[] {
  const line: number[] = [];
  for (let d = 1; d <= range; d++) {
    const sq = offset(from, dx * d, dy * d, s.w, s.h);
    if (sq < 0 || !isStandable(s, sq)) break;
    const t = s.board[sq];
    if (t && t.color === color) break;
    line.push(sq);
  }
  return line;
}

/** Pseudo-legal actions of one piece. attacksOnly limits output to captures. */
export function pieceActions(s: GameState, from: number, out: Action[], attacksOnly = false): void {
  const p = s.board[from];
  if (!p) return;
  const def = s.rs.pieces[p.type];
  if (!def || p.exhausted > 0) return;
  const rules = s.rs.rules;
  const fwd = p.color === 'w' ? 1 : -1;
  const seen = new Set<number>();
  const maxSteps = Math.max(s.w, s.h);

  const push = (a: Extract<Action, { t: 'move' }>) => {
    if (seen.has(a.to)) return;
    seen.add(a.to);
    if (!attacksOnly && def.promotes && rules.promotion.enabled && inZone(s, a.to, p.color)) {
      const choices = rules.promotion.choices.filter((c) => s.rs.pieces[c]);
      if (choices.length) {
        for (const c of choices) out.push({ ...a, promo: c });
        return;
      }
    }
    out.push(a);
  };

  for (const rule of def.moves) {
    if (rule.firstMoveOnly && p.moved) continue;
    const steps = rule.kind === 'leap' ? 1 : rule.max > 0 ? rule.max : maxSteps;
    for (const [vx, vy] of rule.vectors) {
      const dx = vx;
      const dy = vy * fwd;
      for (let d = 1; d <= steps; d++) {
        const to = offset(from, dx * d, dy * d, s.w, s.h);
        if (to < 0) break;
        if (!isStandable(s, to)) {
          if (rule.kind === 'slide' && rule.jump) continue;
          break;
        }
        const t = s.board[to];
        if (t) {
          if (t.color !== p.color && rule.mode !== 'move') push({ t: 'move', from, to });
          if (rule.kind === 'slide' && rule.jump) continue;
          break;
        }
        if (attacksOnly) {
          // Empty squares still count as attacked (castling through check).
          if (rule.mode !== 'move') push({ t: 'move', from, to });
          continue;
        }
        if (rule.mode !== 'capture') {
          const a: Extract<Action, { t: 'move' }> = { t: 'move', from, to };
          if (rule.createsEnPassant && rules.enPassant && d >= 2) a.epTarget = offset(from, dx * (d - 1), dy * (d - 1), s.w, s.h);
          push(a);
        } else if (rule.enPassant && rules.enPassant && s.ep && s.ep.target === to && s.ep.color !== p.color) {
          push({ t: 'move', from, to, epCapture: s.ep.capture });
        }
      }
    }
  }

  for (const ab of def.abilities) {
    if (!ab.enabled || (p.cd[ab.kind] ?? 0) > 0) continue;
    const A = (target?: number): Action => ({ t: 'ability', kind: ab.kind, from, target });
    switch (ab.kind) {
      case 'fireBreath':
        for (const [dx, dy] of DIRS8)
          for (let d = 1; d <= ab.range; d++) {
            const sq = offset(from, dx * d, dy * d, s.w, s.h);
            if (sq < 0 || isWall(s, sq)) break;
            const t = s.board[sq];
            if (t) { if (t.color !== p.color) out.push(A(sq)); break; }
          }
        break;
      case 'wingSweep':
        if (attacksOnly) break;
        if (neighbors(s, from, 1).some((n) => s.board[n] && s.board[n]!.color !== p.color)) out.push(A());
        break;
      case 'detonate':
        if (attacksOnly) break;
        if (!rules.allowSelfRoyalBlast) {
          const before = royalCount(s, p.color);
          const sim = cloneState(s);
          blast(sim, from, [], 0);
          if (royalCount(sim, p.color) < before) break;
        }
        out.push(A());
        break;
      case 'timedFuse':
        if (!attacksOnly && p.fuse === 0) out.push(A());
        break;
      case 'kick':
        if (attacksOnly) break;
        for (const [dx, dy] of DIRS8) {
          const adj = offset(from, dx, dy, s.w, s.h);
          if (adj < 0 || !s.board[adj]) continue;
          const dest = offset(adj, dx, dy, s.w, s.h);
          if (dest < 0 || !isStandable(s, dest) || s.board[dest]) continue;
          out.push(A(adj));
        }
        break;
      case 'placeMine': {
        if (attacksOnly) break;
        const mine = s.mines.filter((m) => m.minerId === p.id).length;
        if (mine >= (ab.max ?? 2)) break;
        for (const n of neighbors(s, from, ab.range)) {
          if (!isStandable(s, n) || s.board[n]) continue;
          if (s.mines.some((m) => m.sq === n && mineVisibleTo(s, m, p.color))) continue;
          out.push(A(n));
        }
        break;
      }
      case 'tunnel':
        if (attacksOnly) break;
        for (const [dx, dy] of DIRS8) {
          const dest = offset(from, dx * ab.range, dy * ab.range, s.w, s.h);
          if (dest >= 0 && isStandable(s, dest) && !s.board[dest]) out.push(A(dest));
        }
        break;
      case 'defuse':
        if (attacksOnly) break;
        for (const n of neighbors(s, from, ab.range))
          if (s.mines.some((m) => m.sq === n && mineVisibleTo(s, m, p.color))) out.push(A(n));
        break;
      case 'dig':
        if (attacksOnly) break;
        for (const n of neighbors(s, from, ab.range)) {
          if (!isStandable(s, n) || s.board[n]) continue;
          if (s.mines.some((m) => m.sq === n && mineVisibleTo(s, m, p.color))) continue;
          out.push(A(n));
        }
        break;
      case 'tongue':
        if (attacksOnly) break;
        // First enemy in a straight line, at least 2 squares away, with nothing in between.
        for (const [dx, dy] of DIRS8)
          for (let d = 1; d <= ab.range; d++) {
            const sq = offset(from, dx * d, dy * d, s.w, s.h);
            if (sq < 0 || isWall(s, sq)) break;
            const t = s.board[sq];
            if (t) { if (t.color !== p.color && d >= 2) out.push(A(sq)); break; }
          }
        break;
      case 'plantHedge': {
        if (attacksOnly) break;
        if (s.hedges.filter((h) => h.by === p.id).length >= (ab.max ?? 2)) break;
        for (const n of neighbors(s, from, ab.range)) {
          if (!isStandable(s, n) || s.board[n]) continue;
          if (s.mines.some((m) => m.sq === n && mineVisibleTo(s, m, p.color))) continue;
          out.push(A(n));
        }
        break;
      }
      case 'tend':
        if (attacksOnly) break;
        if (neighbors(s, from, ab.range).some((n) => s.burning.some((b) => b.sq === n) || s.holes.some((h) => h.sq === n))) out.push(A());
        break;
      case 'stampede':
        // Charge to any square of the line once at least one enemy has been run over.
        for (const [dx, dy] of ORTHO) {
          let hit = false;
          for (const sq of stampedeLine(s, from, p.color, dx, dy, ab.range)) {
            if (s.board[sq]) hit = true;
            if (hit) out.push(A(sq));
          }
        }
        break;
      case 'trumpet':
        if (attacksOnly) break;
        if (neighbors(s, from, ab.range).some((n) => s.board[n] && s.board[n]!.color !== p.color)) out.push(A());
        break;
      case 'multiply':
        if (attacksOnly || p.parentId != null || youngAlive(s, p.id) >= (ab.max ?? 2)) break;
        for (const n of neighbors(s, from, ab.range)) {
          if (!isStandable(s, n) || s.board[n]) continue;
          if (s.mines.some((m) => m.sq === n && mineVisibleTo(s, m, p.color))) continue;
          out.push(A(n));
        }
        break;
      case 'feast':
        if (attacksOnly) break;
        if (neighbors(s, from, ab.range).some((n) => s.board[n]?.color === p.color)) out.push(A());
        break;
      case 'panToss':
        if (attacksOnly) break;
        for (const [dx, dy] of DIRS8)
          for (let d = 1; d <= ab.range; d++) {
            const sq = offset(from, dx * d, dy * d, s.w, s.h);
            if (sq < 0 || isWall(s, sq)) break;
            const t = s.board[sq];
            if (t) { if (t.color !== p.color) out.push(A(sq)); break; }
          }
        break;
    }
  }
}

/** Living pieces spawned by the piece with this id (Multiply). */
function youngAlive(s: GameState, parentId: number): number {
  let n = 0;
  for (const q of s.board) if (q && q.parentId === parentId) n++;
  return n;
}

/**
 * Why the piece on `from` cannot use `kind` right now, in plain words for the UI;
 * null when it can. Judged on what the piece's owner can see.
 */
export function abilityBlockReason(s: GameState, from: number, kind: AbilityKind): string | null {
  const p = s.board[from];
  const def = p ? s.rs.pieces[p.type] : undefined;
  const ab = ability(def, kind);
  if (!p || !ab) return 'not available';
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const cd = p.cd[kind] ?? 0;
  if (cd > 0) return `ready in ${plural(cd, 'turn')}`;
  if (p.exhausted > 0) return 'resting or stunned this turn';
  if (s.result) return 'the game is over';
  if (p.color !== s.turn) return 'not your turn';
  if (legalActions(s).some((a) => a.t === 'ability' && a.kind === kind && a.from === from)) return null;
  const pseudo: Action[] = [];
  pieceActions(viewFor(s, p.color), from, pseudo);
  const mine = pseudo.filter((a) => a.t === 'ability' && a.kind === kind);
  if (mine.length) {
    const view = viewFor(s, p.color);
    const killsKing = mine.every((a) => { const sim = cloneState(view); applyCore(sim, a, []); return royalCount(sim, opp(p.color)) < royalCount(view, opp(p.color)); });
    return killsKing ? 'kings cannot be captured, only checkmated' : 'it would leave your king in check';
  }
  const max = ab.max ?? 2;
  switch (kind) {
    case 'fireBreath':
    case 'panToss':
      return `no enemy in a free straight line within ${plural(ab.range, 'square')}`;
    case 'tongue':
      return `no enemy in a free straight line 2 to ${ab.range} squares away`;
    case 'stampede':
      return `no enemy straight ahead within ${plural(ab.range, 'square')}`;
    case 'wingSweep':
    case 'trumpet':
      return 'no enemy next to it';
    case 'detonate':
      return 'the blast would destroy your own king';
    case 'timedFuse':
      return p.fuse > 0 ? `already armed, goes off in ${plural(p.fuse, 'turn')}` : 'not available';
    case 'kick':
      return 'nothing next to it can be pushed onto a free square';
    case 'placeMine': {
      const n = s.mines.filter((m) => m.minerId === p.id).length;
      return n >= (ab.max ?? 2) ? `all ${plural(ab.max ?? 2, 'mine')} are placed` : 'no free square next to it';
    }
    case 'plantHedge': {
      const n = s.hedges.filter((h) => h.by === p.id).length;
      return n >= max ? `its ${plural(n, 'hedge')} are still growing` : 'no free square next to it';
    }
    case 'multiply': {
      if (p.parentId != null) return 'the young cannot multiply';
      const n = youngAlive(s, p.id);
      return n >= max ? `${plural(n, 'young')} still alive (max ${max})`.replace('youngs', 'young') : 'no free square next to it';
    }
    case 'tunnel':
      return `no free square exactly ${ab.range} squares away in a straight line`;
    case 'defuse':
      return 'no known mine next to it';
    case 'dig':
      return 'no free square next to it';
    case 'tend':
      return 'nothing burning or dug up next to it';
    case 'feast':
      return 'no ally next to it';
  }
}

/** Can `by` capture/destroy whatever stands on `sq` with its next action? */
export function isAttacked(s: GameState, sq: number, by: Color): boolean {
  const buf: Action[] = [];
  for (let from = 0; from < s.board.length; from++) {
    const p = s.board[from];
    if (!p || p.color !== by) continue;
    buf.length = 0;
    pieceActions(s, from, buf, true);
    for (const a of buf) if ((a.t === 'move' ? a.to : a.target) === sq) return true;
    const det = ability(s.rs.pieces[p.type], 'detonate');
    if (det && p.exhausted === 0 && chebyshev(from, sq, s.w) <= det.range) return true;
    const st = ability(s.rs.pieces[p.type], 'stampede');
    if (st && p.exhausted === 0 && !(p.cd.stampede ?? 0)) {
      for (const [dx, dy] of ORTHO) if (stampedeLine(s, from, p.color, dx, dy, st.range).includes(sq)) return true;
    }
  }
  return false;
}

export function royalAttacked(s: GameState, color: Color): boolean {
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (p && p.color === color && s.rs.pieces[p.type]?.royal && isAttacked(s, sq, opp(color))) return true;
  }
  return false;
}

export function inCheck(s: GameState): boolean {
  return s.rs.rules.check && royalAttacked(s, s.turn);
}

function castlingActions(s: GameState, from: number, out: Action[]): void {
  const p = s.board[from]!;
  const y = Math.floor(from / s.w);
  const checkOn = s.rs.rules.check;
  if (checkOn && isAttacked(s, from, opp(p.color))) return;
  for (const dir of [1, -1]) {
    let x = (from % s.w) + dir;
    let partner = -1;
    for (; x >= 0 && x < s.w; x += dir) {
      const sq = y * s.w + x;
      if (!isStandable(s, sq)) break;
      const t = s.board[sq];
      if (t) {
        if (t.color === p.color && !t.moved && s.rs.pieces[t.type]?.castlePartner) partner = sq;
        break;
      }
    }
    if (partner < 0 || Math.abs(partner - from) < 3) continue;
    const kTo = from + 2 * dir;
    const rTo = from + dir;
    if (checkOn && (isAttacked(s, rTo, opp(p.color)) || isAttacked(s, kTo, opp(p.color)))) continue;
    out.push({ t: 'move', from, to: kTo, castle: [partner, rTo] });
  }
}

export function pseudoActions(s: GameState): Action[] {
  const out: Action[] = [];
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (!p || p.color !== s.turn) continue;
    pieceActions(s, sq, out);
    const def = s.rs.pieces[p.type];
    if (s.rs.rules.castling && def?.castles && !p.moved && p.exhausted === 0) castlingActions(s, sq, out);
  }
  return out;
}

/** Fully legal actions for the side to move. Legality is judged on the mover's own view. */
export function legalActions(s: GameState): Action[] {
  if (s.result) return [];
  const pseudo = pseudoActions(s);
  if (!s.rs.rules.check) return pseudo;
  const view = viewFor(s, s.turn);
  const color = s.turn;
  const kings = { w: royalCount(view, 'w'), b: royalCount(view, 'b') };
  return pseudo.filter((a) => {
    const sim = cloneState(view);
    applyCore(sim, a, []);
    // Like real chess: a king is never taken, the game ends with checkmate before that.
    return royalCount(sim, 'w') === kings.w && royalCount(sim, 'b') === kings.b && !royalAttacked(sim, color);
  });
}

export function actionKey(a: Action): string {
  return a.t === 'move' ? `m:${a.from}:${a.to}:${a.promo ?? ''}` : `a:${a.kind}:${a.from}:${a.target ?? ''}`;
}

export function findLegal(s: GameState, a: Action): Action | undefined {
  const k = actionKey(a);
  return legalActions(s).find((l) => actionKey(l) === k);
}

// ---------------------------------------------------------------- effects

/** First free, safe square next to `sq` (fixed order so client and server agree), or -1. */
function escapeSquare(s: GameState, sq: number, avoid: number[] = []): number {
  for (const [dx, dy] of DIRS8) {
    const n = offset(sq, dx, dy, s.w, s.h);
    if (n < 0 || avoid.includes(n) || !isStandable(s, n) || s.board[n]) continue;
    if (s.mines.some((m) => m.sq === n) || s.burning.some((b) => b.sq === n)) continue;
    return n;
  }
  return -1;
}

/** Pieces with spare lives scurry away instead of dying. Returns true if it escaped. */
function trySurvive(s: GameState, sq: number, events: GameEvent[], avoid: number[] = []): boolean {
  const t = s.board[sq];
  const lives = t ? s.rs.pieces[t.type]?.extraLives ?? 0 : 0;
  if (!t || (t.livesUsed ?? 0) >= lives) return false;
  const to = escapeSquare(s, sq, avoid);
  if (to < 0) return false;
  s.board[sq] = null;
  s.board[to] = { ...t, livesUsed: (t.livesUsed ?? 0) + 1 };
  events.push({ e: 'survive', from: sq, to });
  return true;
}

/** With the check rule on, kings are never destroyed: the game ends by checkmate instead. */
const kingsProtected = (s: GameState) => s.rs.rules.check;

function destroy(s: GameState, sq: number, events: GameEvent[], step: number, cause: string, spareRoyals = false): void {
  const t = s.board[sq];
  if (!t) return;
  if (spareRoyals && s.rs.pieces[t.type]?.royal) return;
  if (trySurvive(s, sq, events)) return;
  s.board[sq] = null;
  events.push({ e: 'destroy', sq, type: t.type, color: t.color, step, cause });
}

function enterSquare(s: GameState, sq: number, events: GameEvent[], step = 0): void {
  if (!s.board[sq]) return;
  const hit = s.mines.filter((m) => m.sq === sq);
  if (hit.length) {
    events.push({ e: 'mineTrigger', sq });
    destroy(s, sq, events, step, 'mine', kingsProtected(s));
    s.mines = s.rs.rules.persistentMines
      ? s.mines.map((m) => (m.sq === sq ? { ...m, revealed: true } : m))
      : s.mines.filter((m) => m.sq !== sq);
    return;
  }
  if (s.burning.some((b) => b.sq === sq)) {
    events.push({ e: 'burn', sq });
    destroy(s, sq, events, step, 'fire', kingsProtected(s));
  }
}

function pushPiece(s: GameState, from: number, dx: number, dy: number, events: GameEvent[]): void {
  const t = s.board[from];
  if (!t) return;
  const to = offset(from, dx, dy, s.w, s.h);
  if (to < 0 || isHole(s, to)) {
    events.push({ e: 'push', from, to: to < 0 ? from : to });
    destroy(s, from, events, 0, 'fall');
    return;
  }
  if (isWall(s, to) || s.board[to]) return;
  s.board[from] = null;
  s.board[to] = t;
  events.push({ e: 'push', from, to });
  enterSquare(s, to, events);
}

/** Explode the bomb on `origin`; bombs caught in the blast chain one step later. */
export function blast(s: GameState, origin: number, events: GameEvent[], baseStep: number, spareRoyals = false): void {
  const queue: { sq: number; step: number }[] = [{ sq: origin, step: baseStep }];
  const exploding = new Set([origin]);
  while (queue.length) {
    const { sq, step } = queue.shift()!;
    const bomb = s.board[sq];
    const radius = ability(bomb ? s.rs.pieces[bomb.type] : undefined, 'detonate')?.range ?? 1;
    s.board[sq] = null;
    events.push({ e: 'explode', sq, step, radius });
    for (const n of neighbors(s, sq, radius)) {
      const t = s.board[n];
      if (!t || exploding.has(n)) continue;
      if (s.rs.rules.chainReaction && ability(s.rs.pieces[t.type], 'detonate')) {
        exploding.add(n);
        queue.push({ sq: n, step: step + 1 });
        continue;
      }
      destroy(s, n, events, step, 'blast', spareRoyals);
    }
  }
}

function setPiece(s: GameState, sq: number, patch: Partial<Piece>): Piece {
  const p = { ...s.board[sq]!, ...patch };
  s.board[sq] = p;
  return p;
}

/** Apply the action's direct effects (no turn bookkeeping). Mutates `s`. */
export function applyCore(s: GameState, a: Action, events: GameEvent[]): void {
  const p = s.board[a.from];
  if (!p) return;
  const def = s.rs.pieces[p.type];
  if (s.ep && s.ep.color !== p.color) s.ep = null;
  const before = pieceCount(s, 'w') + pieceCount(s, 'b');

  if (a.t === 'move') {
    let captured = false;
    if (a.epCapture != null && s.board[a.epCapture]) {
      const c = s.board[a.epCapture]!;
      if (!trySurvive(s, a.epCapture, events, [a.to])) {
        events.push({ e: 'capture', sq: a.epCapture, type: c.type, color: c.color });
        s.board[a.epCapture] = null;
        captured = true;
      }
    } else if (s.board[a.to]) {
      const c = s.board[a.to]!;
      if (!trySurvive(s, a.to, events)) {
        events.push({ e: 'capture', sq: a.to, type: c.type, color: c.color });
        captured = true;
      }
    }
    s.board[a.from] = null;
    s.board[a.to] = { ...p, moved: true, type: a.promo ?? p.type };
    events.push({ e: 'move', from: a.from, to: a.to });
    if (a.promo) events.push({ e: 'promote', sq: a.to, type: a.promo });
    if (a.castle) {
      const [rf, rt] = a.castle;
      const r = s.board[rf];
      if (r) {
        s.board[rf] = null;
        s.board[rt] = { ...r, moved: true };
        events.push({ e: 'castle', from: rf, to: rt });
      }
    }
    if (a.epTarget != null && a.epTarget >= 0) s.ep = { target: a.epTarget, capture: a.to, color: p.color };
    s.halfmove = captured || def?.promotes ? 0 : s.halfmove + 1;
    enterSquare(s, a.to, events);
    if (a.castle) enterSquare(s, a.castle[1], events);
    return;
  }

  const ab = ability(def, a.kind);
  if (!ab) return;
  const cd = { ...p.cd };
  if (ab.cooldown > 0) cd[a.kind] = ab.cooldown;
  const me = setPiece(s, a.from, { cd, exhausted: ab.exhausts ? 2 : p.exhausted });
  const tgt = a.target ?? -1;

  switch (a.kind) {
    case 'fireBreath': {
      events.push({ e: 'fire', from: a.from, to: tgt });
      destroy(s, tgt, events, 0, 'fire');
      if (s.rs.rules.scorchedEarth) {
        const dx = Math.sign((tgt % s.w) - (a.from % s.w));
        const dy = Math.sign(Math.floor(tgt / s.w) - Math.floor(a.from / s.w));
        let sq = a.from;
        do {
          sq = offset(sq, dx, dy, s.w, s.h);
          if (sq < 0) break;
          s.burning = s.burning.filter((b) => b.sq !== sq);
          s.burning.push({ sq, left: Math.max(1, s.rs.rules.scorchDuration) * 2, owner: p.color });
        } while (sq !== tgt);
      }
      break;
    }
    case 'wingSweep':
      events.push({ e: 'sweep', sq: a.from });
      for (const [dx, dy] of DIRS8) {
        const adj = offset(a.from, dx, dy, s.w, s.h);
        const t = adj >= 0 ? s.board[adj] : null;
        if (t && t.color !== p.color) pushPiece(s, adj, dx * ab.range, dy * ab.range, events);
      }
      break;
    case 'detonate':
      blast(s, a.from, events, 0);
      break;
    case 'timedFuse':
      setPiece(s, a.from, { fuse: Math.max(1, ab.duration ?? 2), armedNow: true });
      events.push({ e: 'arm', sq: a.from });
      break;
    case 'kick': {
      const dx = (tgt % s.w) - (a.from % s.w);
      const dy = Math.floor(tgt / s.w) - Math.floor(a.from / s.w);
      pushPiece(s, tgt, dx, dy, events);
      break;
    }
    case 'placeMine':
      s.mines = [...s.mines, { sq: tgt, owner: p.color, minerId: p.id, revealed: false }];
      events.push({ e: 'minePlace', sq: tgt, owner: p.color });
      break;
    case 'tunnel':
      s.board[a.from] = null;
      s.board[tgt] = { ...me, moved: true };
      events.push({ e: 'tunnel', from: a.from, to: tgt });
      enterSquare(s, tgt, events);
      break;
    case 'defuse':
      s.mines = s.mines.filter((m) => m.sq !== tgt);
      events.push({ e: 'defuse', sq: tgt });
      break;
    case 'dig':
      s.mines = s.mines.filter((m) => m.sq !== tgt);
      s.holes = [...s.holes, { sq: tgt, left: Math.max(1, ab.duration ?? 3) * 2, owner: p.color }];
      events.push({ e: 'dig', sq: tgt });
      break;
    case 'tongue': {
      const to = offset(a.from, Math.sign((tgt % s.w) - (a.from % s.w)), Math.sign(Math.floor(tgt / s.w) - Math.floor(a.from / s.w)), s.w, s.h);
      const t = s.board[tgt];
      if (!t || to < 0) break;
      events.push({ e: 'tongue', from: a.from, target: tgt, to });
      if (isHole(s, to)) { destroy(s, tgt, events, 0, 'fall'); break; }
      s.board[tgt] = null;
      s.board[to] = t;
      enterSquare(s, to, events);
      break;
    }
    case 'plantHedge':
      s.hedges = [...s.hedges, { sq: tgt, left: Math.max(1, ab.duration ?? 3) * 2, owner: p.color, by: p.id }];
      events.push({ e: 'hedge', sq: tgt });
      break;
    case 'tend':
      for (const n of neighbors(s, a.from, ab.range)) {
        if (!s.burning.some((b) => b.sq === n) && !s.holes.some((h) => h.sq === n)) continue;
        s.burning = s.burning.filter((b) => b.sq !== n);
        s.holes = s.holes.filter((h) => h.sq !== n);
        events.push({ e: 'tend', sq: n });
      }
      break;
    case 'stampede': {
      const dx = Math.sign((tgt % s.w) - (a.from % s.w));
      const dy = Math.sign(Math.floor(tgt / s.w) - Math.floor(a.from / s.w));
      const path: number[] = [];
      for (let sq = a.from; sq !== tgt;) { sq = offset(sq, dx, dy, s.w, s.h); path.push(sq); }
      for (const sq of path) {
        const t = s.board[sq];
        if (!t || trySurvive(s, sq, events, [...path, a.from])) continue;
        events.push({ e: 'capture', sq, type: t.type, color: t.color });
        s.board[sq] = null;
      }
      s.board[a.from] = null;
      s.board[tgt] = { ...me, moved: true };
      events.push({ e: 'stampede', from: a.from, to: tgt });
      enterSquare(s, tgt, events);
      break;
    }
    case 'trumpet': {
      const stunned: number[] = [];
      for (const n of neighbors(s, a.from, ab.range)) {
        const t = s.board[n];
        if (!t || t.color === p.color) continue;
        // Exhaustion ticks down at the end of its owner's turn, so 1 = skips its next turn.
        s.board[n] = { ...t, exhausted: Math.max(t.exhausted, 1) };
        stunned.push(n);
      }
      events.push({ e: 'trumpet', sq: a.from, stunned });
      break;
    }
    case 'multiply': {
      // The young are fully grown but cannot multiply themselves.
      s.board[tgt] = { id: s.nextId++, type: p.type, color: p.color, moved: true, cd: {}, exhausted: 0, fuse: 0, parentId: me.id };
      events.push({ e: 'spawn', from: a.from, sq: tgt });
      enterSquare(s, tgt, events);
      break;
    }
    case 'feast': {
      const fed: number[] = [];
      for (const n of neighbors(s, a.from, ab.range)) {
        const t = s.board[n];
        if (!t || t.color !== p.color) continue;
        s.board[n] = { ...t, cd: {}, exhausted: 0 };
        fed.push(n);
      }
      events.push({ e: 'feast', sq: a.from, fed });
      break;
    }
    case 'panToss': {
      const t = s.board[tgt];
      if (t) s.board[tgt] = { ...t, exhausted: Math.max(t.exhausted, 1) };
      events.push({ e: 'pan', from: a.from, target: tgt });
      break;
    }
  }
  const after = pieceCount(s, 'w') + pieceCount(s, 'b');
  s.halfmove = after < before ? 0 : s.halfmove + 1;
}

/** End the current player's turn: tick cooldowns, fuses and timed squares. */
function endTurn(s: GameState, events: GameEvent[]): void {
  const color = s.turn;
  const fuses: number[] = [];
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (!p || p.color !== color) continue;
    const cd: Piece['cd'] = {};
    for (const k in p.cd) {
      const v = (p.cd[k as AbilityKind] ?? 0) - 1;
      if (v > 0) cd[k as AbilityKind] = v;
    }
    let fuse = p.fuse;
    if (fuse > 0 && !p.armedNow) {
      fuse--;
      if (fuse === 0) fuses.push(sq);
    }
    s.board[sq] = { ...p, cd, exhausted: Math.max(0, p.exhausted - 1), fuse, armedNow: false };
  }
  s.burning = s.burning.map((b) => ({ ...b, left: b.left - 1 })).filter((b) => b.left > 0);
  s.holes = s.holes.map((b) => ({ ...b, left: b.left - 1 })).filter((b) => b.left > 0);
  s.hedges = s.hedges.map((b) => ({ ...b, left: b.left - 1 })).filter((b) => b.left > 0);
  let step = 0;
  for (const sq of fuses) {
    if (!s.board[sq]) continue;
    const n = events.length;
    // A fuse runs out at the end of the turn, so the other side never had a chance to react.
    blast(s, sq, events, step, kingsProtected(s));
    for (let i = n; i < events.length; i++) {
      const ev = events[i];
      if ('step' in ev) step = Math.max(step, ev.step + 1);
    }
  }
  s.turn = opp(color);
  s.actionsLeft = Math.max(1, s.rs.rules.movesPerTurn);
  if (color === 'b') s.turnNumber++;
}

function royalResult(s: GameState, mover: Color): GameResult | null {
  const wc = s.rs.rules.winConditions;
  const lost: Color[] = [];
  for (const c of ['w', 'b'] as Color[]) {
    if (pieceCount(s, c) === 0) { lost.push(c); continue; }
    const start = s.royalsStart[c];
    if (!start) continue;
    const now = royalCount(s, c);
    if ((wc.captureAnyRoyal && now < start) || (wc.captureAllRoyals && now === 0)) lost.push(c);
  }
  if (lost.length === 2) return { winner: null, reason: 'mutual destruction' };
  if (lost.length === 1) {
    const reason = pieceCount(s, lost[0]) === 0 ? 'annihilation' : 'royal captured';
    return { winner: opp(lost[0]), reason };
  }
  if (wc.reachSquare) {
    for (const c of [mover, opp(mover)]) {
      for (const name of s.rs.rules.reachSquares[c]) {
        const sq = parseSq(name, s.w, s.h);
        const p = sq >= 0 ? s.board[sq] : null;
        if (!p || p.color !== c) continue;
        const types = s.rs.rules.reachTypes;
        if (types.length ? types.includes(p.type) : s.rs.pieces[p.type]?.royal) return { winner: c, reason: 'reached target' };
      }
    }
  }
  return null;
}

function insufficient(s: GameState): boolean {
  const rest: string[] = [];
  for (const p of s.board) if (p && !s.rs.pieces[p.type]?.royal) rest.push(p.type);
  if (rest.length === 0) return true;
  return rest.length === 1 && (rest[0] === 'bishop' || rest[0] === 'knight');
}

export interface ApplyResult {
  state: GameState;
  events: GameEvent[];
}

/** Apply a (legal) action and run all turn, timer and game-end logic. */
export function applyAction(s: GameState, a: Action): ApplyResult {
  const n = cloneState(s);
  const events: GameEvent[] = [];
  const mover = s.turn;
  applyCore(n, a, events);
  n.ply++;
  n.actionsLeft--;
  n.result = royalResult(n, mover);
  if (n.result) return { state: n, events };

  if (n.actionsLeft <= 0 || legalActions(n).length === 0) {
    endTurn(n, events);
    n.history.push(positionHash(n));
    n.result = royalResult(n, mover);
    if (n.result) return { state: n, events };
  }

  const r = n.rs.rules;
  if (legalActions(n).length === 0) {
    if (inCheck(n) && r.winConditions.checkmate) n.result = { winner: opp(n.turn), reason: 'checkmate' };
    else if (r.draws.stalemate === 'loss') n.result = { winner: opp(n.turn), reason: 'stalemate' };
    else n.result = { winner: null, reason: 'stalemate' };
  } else if (r.draws.fiftyMove && n.halfmove >= 100) {
    n.result = { winner: null, reason: '50-move rule' };
  } else if (r.draws.threefold && n.history.filter((h) => h === n.history[n.history.length - 1]).length >= 3) {
    n.result = { winner: null, reason: 'threefold repetition' };
  } else if (r.draws.insufficientMaterial && insufficient(n)) {
    n.result = { winner: null, reason: 'insufficient material' };
  }
  return { state: n, events };
}

// ---------------------------------------------------------------- notation

export function actionToSan(s: GameState, a: Action, after?: GameState, viewer: Color | null = null): string {
  const p = s.board[a.from];
  if (!p) return '?';
  const def = s.rs.pieces[p.type];
  const L = def?.letter ?? '?';
  const name = (sq: number) => sqName(sq, s.w);
  let san: string;
  if (a.t === 'move') {
    if (a.castle) san = a.to > a.from ? 'O-O' : 'O-O-O';
    else {
      const cap = !!s.board[a.to] || a.epCapture != null;
      if (!L) san = (cap ? name(a.from)[0] + 'x' : '') + name(a.to);
      else {
        const others = legalActions(s).filter(
          (o) => o.t === 'move' && o.to === a.to && o.from !== a.from && s.board[o.from]?.type === p.type,
        );
        let dis = '';
        if (others.length) {
          const f = name(a.from);
          const sameFile = others.some((o) => name(o.from)[0] === f[0]);
          const sameRank = others.some((o) => name(o.from).slice(1) === f.slice(1));
          dis = !sameFile ? f[0] : !sameRank ? f.slice(1) : f;
        }
        san = L + dis + (cap ? 'x' : '') + name(a.to);
      }
      if (a.promo) san += '=' + (s.rs.pieces[a.promo]?.letter || a.promo[0].toUpperCase());
    }
  } else {
    const code = ABILITY_INFO[a.kind].code;
    const hide = a.kind === 'placeMine' && viewer !== p.color;
    san = `${L || def?.name[0] || '?'}${name(a.from)} ${code}` + (a.target != null && a.target >= 0 ? ' ' + (hide ? '?' : name(a.target)) : '');
  }
  if (after) {
    if (after.result?.reason === 'checkmate') san += '#';
    else if (!after.result && inCheck(after)) san += '+';
  }
  return san;
}

// ---------------------------------------------------------------- admin tools

/** A nuke: siren first, then the bomber flies over, then the bomb hits. Server and clients share this timeline. */
export const NUKE_ALARM_MS = 2200;
export const NUKE_FLIGHT_MS = 2300;

/** Admin-only actions (checked by the server for online games). */
export type AdminAction = { kind: 'nuke' } | { kind: 'spawn'; type: string; sq: number } | { kind: 'win' };

/** Why an admin action cannot be done right now, or null. */
export function adminBlockReason(s: GameState, color: Color, a: AdminAction): string | null {
  if (s.result) return 'the game is over';
  if (a.kind === 'spawn') {
    if (!s.rs.pieces[a.type]) return 'unknown piece';
    if (a.sq < 0 || a.sq >= s.board.length || !isStandable(s, a.sq) || s.board[a.sq]) return 'pick an empty square';
    if (s.rs.pieces[a.type].royal) return 'kings cannot be spawned';
  }
  if (!['nuke', 'spawn', 'win'].includes(a.kind)) return 'unknown admin action';
  void color;
  return null;
}

/**
 * Apply an admin action for `color`. Nuke wipes out every piece but that side's kings and wins;
 * Win just wins; Spawn puts a new piece of that colour on an empty square (the turn does not change).
 */
export function applyAdmin(s: GameState, color: Color, a: AdminAction): ApplyResult {
  const why = adminBlockReason(s, color, a);
  if (why) throw new Error(why);
  const n = cloneState(s);
  const events: GameEvent[] = [];
  if (a.kind === 'spawn') {
    n.board[a.sq] = { id: n.nextId++, type: a.type, color, moved: true, cd: {}, exhausted: 0, fuse: 0 };
    events.push({ e: 'spawn', from: a.sq, sq: a.sq });
    // A spawned piece may give mate at once.
    if (n.turn !== color && legalActions(n).length === 0) {
      n.result = inCheck(n) ? { winner: color, reason: 'checkmate' } : { winner: null, reason: 'stalemate' };
    }
    return { state: n, events };
  }
  if (a.kind === 'nuke') {
    events.push({ e: 'nuke', by: color });
    for (let sq = 0; sq < n.board.length; sq++) {
      const p = n.board[sq];
      if (!p || (p.color === color && n.rs.pieces[p.type]?.royal)) continue;
      n.board[sq] = null;
      events.push({ e: 'destroy', sq, type: p.type, color: p.color, step: 1, cause: 'nuke' });
    }
    n.mines = [];
    n.result = { winner: color, reason: 'nuke' };
    return { state: n, events };
  }
  n.result = { winner: color, reason: 'admin win' };
  return { state: n, events };
}
